import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Waves, ArrowRight, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

export default function ParkPage() {
  const { slug } = useParams();
  const [park, setPark] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/parks/${slug}`).then(r => { setPark(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-[#6B726A]">Loading...</div></div>;
  if (!park) return <div className="min-h-screen flex items-center justify-center"><div className="text-[#6B726A]">Park not found</div></div>;

  return (
    <div data-testid="park-page">
      {/* Hero */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img src={park.image_url} alt={park.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3F33]/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24 max-w-[1440px] mx-auto">
          <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
            <MapPin className="w-3 h-3" /> {park.location}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-white mb-3" data-testid="park-title">{park.name}</h1>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Waves className="w-4 h-4" /> {park.distance_to_beach} to beach
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">About This Park</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-6">{park.name}</h2>
            <p className="text-base leading-relaxed text-[#6B726A]" data-testid="park-description">{park.description}</p>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#F0EBE1] border border-[#E6D5B8] p-6 rounded-lg">
              <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Park Facilities</h3>
              <div className="space-y-2" data-testid="park-facilities">
                {park.facilities?.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#1F2A25]">
                    <CheckCircle className="w-4 h-4 text-[#2D6A4F] flex-shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caravans at this park */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F0EBE1]" data-testid="park-caravans">
        <div className="max-w-[1440px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">Stay Here</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-8">Caravans at {park.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(park.caravans || []).map(c => (
              <Link key={c.id} to={`/caravans/${c.slug}`} className="group" data-testid={`park-caravan-${c.slug}`}>
                <div className="bg-white border border-[#E6D5B8] overflow-hidden rounded-xl transition-transform duration-300 hover:-translate-y-1">
                  <div className="img-zoom-wrapper aspect-[4/3]">
                    <img src={c.images?.[0]?.url} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-medium text-[#1F2A25] group-hover:text-[#CC5A3D] transition-colors mb-2">{c.name}</h3>
                    <div className="flex items-center gap-4 text-xs text-[#6B726A] mb-3">
                      <span><Users className="w-3 h-3 inline mr-1" />Sleeps {c.sleeps}</span>
                      <span>{c.bedrooms} bed</span>
                      <span>{c.bathrooms} bath</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-heading font-medium text-[#1E3F33]">From &pound;{c.pricing?.off_peak}<span className="text-xs text-[#6B726A] font-body">/night</span></span>
                      <span className="text-sm text-[#CC5A3D] font-medium flex items-center gap-1">Book Now <ArrowRight className="w-3 h-3" /></span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            {(!park.caravans || park.caravans.length === 0) && (
              <p className="text-[#6B726A] col-span-full">No caravans listed for this park yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
